import {
  PLAN_LIMIT_REACHED_CODE,
  PLAN_LIMITS,
  type PlanLimitType,
} from '@featit/shared/billing-plan-limits';
import { ENTITLEMENT_REQUIRED_CODE } from '@featit/shared/entitlements';
import { DEFAULT_LOCALE, formatNumber } from '@featit/shared/i18n/core';
import { failures } from '@featit/shared/i18n/messages/en/failures';
import { registerEnglishArea } from '@/i18n/catalogue';
import { englishT } from '@/i18n/LocaleProvider';
import { pageLocale, pageT } from '@/i18n/pageTranslator';
import { canOfferPurchases } from '@/shell/shellDetection';

registerEnglishArea('failures', failures);

/**
 * I-1712 (AR-7.12, App Review Guideline 3.1.1): the sentence a plan's refusal says in the iOS app.
 *
 * The server writes its refusal for every client — "Remove one or choose a larger plan", "It is
 * available on a paid plan" — and does not know which one asked. Both refusals carry a stable code
 * (`PLAN_LIMIT_REACHED`, with the limit, its cap and the count; `ENTITLEMENT_REQUIRED`), so the
 * app draws its own sentence from the code instead: the limit as a fact, and no plan to buy.
 *
 * Null in a browser, the desktop app and Android, which keep the server's sentence, and for any
 * failure that is not a plan's refusal. Said in the page's language (`failures.planLimit`), its
 * counts grouped as that language groups them. Read by the two places a refusal's sentence is read,
 * `actionFailure.ts` and `ApiService.httpErrorMessage`.
 */
export function planRefusalSentence(error: unknown): string | null {
  if (canOfferPurchases()) return null;
  const refusal = refusalBody(error);
  if (!refusal) return null;
  if (refusal.code === ENTITLEMENT_REQUIRED_CODE) {
    return pageT(englishT)('failures.planLimit.entitlement');
  }
  return limitSentence(refusal);
}

interface IRefusalBody {
  code: string;
  limit?: unknown;
  cap?: unknown;
  count?: unknown;
}

/** The object that carries the code: a REST body, or a GraphQL error's extensions or original error. */
function refusalBody(error: unknown): IRefusalBody | null {
  const candidate = error as {
    response?: { data?: unknown };
    extensions?: { originalError?: unknown } & Record<string, unknown>;
  } | null;
  for (const body of [
    candidate?.response?.data,
    candidate?.extensions?.originalError,
    candidate?.extensions,
  ]) {
    const code = (body as { code?: unknown } | null | undefined)?.code;
    if (code === PLAN_LIMIT_REACHED_CODE || code === ENTITLEMENT_REQUIRED_CODE) {
      return body as IRefusalBody;
    }
  }
  return null;
}

function count(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function limitSentence(refusal: IRefusalBody): string {
  const t = pageT(englishT);
  const limit = PLAN_LIMITS.includes(refusal.limit as PlanLimitType)
    ? (refusal.limit as PlanLimitType)
    : null;
  const cap = count(refusal.cap);
  const used = count(refusal.count);
  if (limit === null || cap === null) return t('failures.planLimit.atLimit');
  // T-0866: the plural is chosen by the number; the number is written as the reader's language writes it.
  const locale = pageLocale(DEFAULT_LOCALE);
  const capCount = formatNumber(locale, cap);
  const counts =
    used === null
      ? { cap, capCount }
      : { cap, capCount, used, usedCount: formatNumber(locale, used) };
  switch (limit) {
    case 'active_agents_per_month':
      return t('failures.planLimit.activeAgents', counts);
    case 'work_areas':
      return t(
        used === null ? 'failures.planLimit.workAreas' : 'failures.planLimit.workAreasUsed',
        counts,
      );
    case 'core_seats':
      return t(
        used === null ? 'failures.planLimit.coreSeats' : 'failures.planLimit.coreSeatsUsed',
        counts,
      );
    case 'active_reporters_per_month':
      return t('failures.planLimit.reporters', counts);
  }
}
